/**
 * Cards of a project not on screen that an agent presented (plan 13), each
 * with its presenter's owner key. Denker shows cards of any of the user's
 * projects wherever the user is: such a card opens in Focus or in its own
 * window. A guide step's `show_only` closes the presenter's earlier ones,
 * also a card in its own window (Focus rounds never reach those) and a
 * Focus card that lost its owner tag (an untagged frame counts as the
 * user's), so only the named frames stay. One the user took (`kept`)
 * stays theirs. Per window: the owner window presents and opens them.
 */
import {
  closeFloatingFrame,
  useFloatingFrameStore,
} from "@/desktop/floating-frame-store";
import { focusOwnerKey } from "@/lib/agent-focus-origin";
import type { PresentFramesRequest } from "@/lib/canvas-presentation-types";
import {
  recordSessionLatency,
  sessionLatencyEnabled,
} from "@/lib/session-latency-trace";
import { useFramesStore } from "@/stores/frames-store";
import { usePresentationStore } from "@/stores/presentation-store";
import { useProjectStore } from "@/stores/project-store";

/** Who presents: the agent, its execution and its owner stream. */
export type CardPresenter = Pick<
  PresentFramesRequest,
  "agentId" | "executionId" | "ownerId"
>;

const MAX_NOTED_CARDS = 64;
const presenters = new Map<string, string>();

function notedCard(frameId: string, ownerKey: string): void {
  presenters.delete(frameId);
  presenters.set(frameId, ownerKey);
  const oldest = presenters.keys().next().value;
  if (presenters.size > MAX_NOTED_CARDS && oldest) presenters.delete(oldest);
}

/** The cards `request`'s agent presents from a project not on screen. */
export function notePresentedCards(
  request: CardPresenter,
  frameIds: readonly string[],
): void {
  const ownerKey = focusOwnerKey(request);
  const viewed = useProjectStore.getState().currentProject?.id;
  if (!ownerKey || !viewed) return;
  const { framesById } = useFramesStore.getState();
  for (const id of frameIds) {
    const project = framesById[id]?.spaceId;
    // A card of the viewed project is on the wall: not one to close.
    if (project === viewed) presenters.delete(id);
    else if (project) notedCard(id, ownerKey);
  }
}

/** An agent opened a card of a project not on screen in its own window. */
export function noteCardWindow(
  presenter: CardPresenter,
  frameId: string,
): void {
  const ownerKey = focusOwnerKey(presenter);
  if (ownerKey) notedCard(frameId, ownerKey);
}

/**
 * A guide step's `show_only`: the presenter's earlier cards close, except
 * the requested ones. Its windows close here; a Focus card without a tag
 * gets its presenter's tag back, so the round replaces it.
 */
export function releasePresenterCards(
  request: PresentFramesRequest,
  frameIds: readonly string[],
): void {
  const ownerKey = focusOwnerKey(request);
  if (!request.replacesRound || !ownerKey) return;
  const requested = new Set(frameIds);
  const earlier = (id: string) =>
    !requested.has(id) && presenters.get(id) === ownerKey;
  for (const window of Object.values(useFloatingFrameStore.getState().frames)) {
    if (window.otherProject && earlier(window.frameId)) {
      closeFloatingFrame(window.frameId);
      presenters.delete(window.frameId);
    }
  }
  usePresentationStore.setState((state) => {
    const focus = state.presentation;
    if (focus?.variant !== "focus") return state;
    const lost = focus.frameIds.filter((id) => {
      const tag = focus.frameTags?.[id];
      return earlier(id) && !tag?.owner && !tag?.kept;
    });
    if (lost.length === 0) return state;
    return {
      presentation: {
        ...focus,
        frameTags: {
          ...focus.frameTags,
          ...Object.fromEntries(
            lost.map((id) => [id, { owner: ownerKey, round: null }]),
          ),
        },
      },
    };
  });
}

/** Focus and other-project windows as `id=owner` (`user`, `untagged`). */
function focusRoundSnapshot(): string[] {
  const focus = usePresentationStore.getState().presentation;
  const inFocus =
    focus?.variant === "focus"
      ? focus.frameIds.map((id) => {
          const tag = focus.frameTags?.[id];
          const owner =
            tag?.kept === "user" ? "user" : (tag?.owner ?? "untagged");
          return `${id}=${owner}`;
        })
      : [];
  const windows = Object.values(useFloatingFrameStore.getState().frames)
    .filter((window) => window.otherProject)
    .map(
      (window) =>
        `${window.frameId}=window:${presenters.get(window.frameId) ?? "unknown"}`,
    );
  return [...inFocus, ...windows];
}

/** What showed before an agent's presentation, when its round is traced. */
export function focusRoundBefore(
  request: PresentFramesRequest,
): string[] | null {
  return sessionLatencyEnabled() && focusOwnerKey(request)
    ? focusRoundSnapshot()
    : null;
}

/** One diagnostic line per agent presentation: what showed before and after. */
export function traceFocusRound(
  request: PresentFramesRequest,
  before: string[] | null,
): void {
  if (!before) return;
  recordSessionLatency("presentation.focus_round", {
    presentationId: request.presentationDeliveryId,
    executionId: request.executionId,
    frames: request.frameIds,
    before,
    after: focusRoundSnapshot(),
  });
}

export function resetPresentedCardsForTests(): void {
  presenters.clear();
}
